import { useState, useEffect, useRef } from 'react';

/**
 * RoleEditModal
 * 
 * Modal for editing role name and description with reason.
 * Note: Capability management is handled separately on the role detail page.
 */
export default function RoleEditModal({
  open,
  onClose,
  onConfirm,
  role,
  loading = false,
  error = null,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const nameInputRef = useRef(null);
  const modalRef = useRef(null);

  // Initialize form fields when role data is available
  useEffect(() => {
    if (role && open) {
      setName(role.name || '');
      setDescription(role.description || '');
      setReason('');
    }
  }, [role, open]);

  // Focus name field when modal opens
  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      nameInputRef.current?.focus();
    }, 100);
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setReason('');
    }
  }, [open]);

  if (!open || !role) {
    return null;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!name.trim() || !reason.trim()) {
      return;
    }
    
    if (onConfirm && typeof onConfirm === 'function') {
      onConfirm({
        name: name.trim(),
        description: description.trim() || null,
        reason: reason.trim(),
      });
    }
  };

  // Role name validation (lowercase alphanumeric + hyphens/underscores)
  const nameRegex = /^[a-z0-9_-]*$/;
  const isValidName = name.trim() && nameRegex.test(name.trim());
  const hasChanges = name !== (role.name || '') || description !== (role.description || '');
  const isSystemRole = role.systemRole === true;
  const canSubmit = isValidName && reason.trim().length > 0 && hasChanges && !loading && !isSystemRole;

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
      role="presentation"
      aria-hidden={!open}
    >
      <div
        ref={modalRef}
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="role-edit-title"
      >
        <div style={styles.header}>
          <h2 id="role-edit-title" style={styles.title}>Edit Role</h2>
          {!loading && (
            <button
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Close dialog"
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.content}>
            {isSystemRole && (
              <div style={styles.warning} role="alert">
                <p><strong>System Role:</strong> This is a system role and cannot be modified.</p>
              </div>
            )}

            <p style={styles.description}>
              Update role information. A reason is required for audit logging.
            </p>

            <div style={styles.formGroup}>
              <label htmlFor="role-edit-name" style={styles.label}>
                Role Name <span style={styles.required}>*</span>
              </label>
              <input
                id="role-edit-name"
                ref={nameInputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase())}
                placeholder="operator"
                style={{
                  ...styles.input,
                  ...(name.trim() && !isValidName && styles.inputError),
                  ...(isSystemRole && styles.inputDisabled),
                }}
                disabled={loading || isSystemRole}
                aria-required="true"
                aria-invalid={name.trim() && !isValidName}
                aria-describedby={isSystemRole ? 'system-role-help' : undefined}
              />
              {name.trim() && !isValidName && (
                <p style={styles.errorText}>Role name must be lowercase alphanumeric with hyphens or underscores only</p>
              )}
              {isSystemRole && (
                <p id="system-role-help" style={styles.helpText}>
                  System roles cannot be renamed.
                </p>
              )}
              {!isSystemRole && (
                <p style={styles.helpText}>Lowercase letters, numbers, hyphens, and underscores only.</p>
              )}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="role-edit-description" style={styles.label}>
                Description
              </label>
              <input
                id="role-edit-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional role description"
                style={{
                  ...styles.input,
                  ...(isSystemRole && styles.inputDisabled),
                }}
                disabled={loading || isSystemRole}
              />
              {isSystemRole && (
                <p style={styles.helpText}>System roles cannot be modified.</p>
              )}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="role-edit-reason" style={styles.label}>
                Reason <span style={styles.required}>*</span>
              </label>
              <textarea
                id="role-edit-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter a reason for updating this role..."
                style={{
                  ...styles.textarea,
                  ...(!reason.trim() && styles.textareaError),
                  ...(isSystemRole && styles.textareaDisabled),
                }}
                rows={4}
                disabled={loading || isSystemRole}
                aria-required="true"
              />
              {!reason.trim() && !isSystemRole && (
                <p style={styles.errorText}>Reason is required</p>
              )}
              {!isSystemRole && (
                <p style={styles.helpText}>
                  This reason will be recorded in the audit log.
                </p>
              )}
            </div>

            {error && (
              <div style={styles.error} role="alert" aria-live="polite">
                <p>{error}</p>
              </div>
            )}
          </div>

          <div style={styles.footer}>
            <button
              onClick={onClose}
              disabled={loading}
              style={styles.cancelButton}
              type="button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || loading}
              style={{
                ...styles.submitButton,
                ...((!canSubmit || loading) && styles.submitButtonDisabled),
              }}
              aria-disabled={!canSubmit || loading}
            >
              {loading ? 'Updating...' : 'Update Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    width: '90%',
    maxWidth: '540px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevent modal from scrolling, only content scrolls
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0, // Header doesn't shrink
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#111827',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
    lineHeight: 1,
    borderRadius: '0.25rem',
    transition: 'background-color 0.2s',
  },
  content: {
    padding: '1.5rem',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0, // Allow flex item to shrink below content size
  },
  description: {
    marginTop: 0,
    marginBottom: '1.5rem',
    color: '#6b7280',
    fontSize: '0.875rem',
  },
  warning: {
    marginBottom: '1.5rem',
    padding: '0.75rem',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    border: '1px solid #fbbf24',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#374151',
    fontSize: '0.875rem',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    color: '#111827',
    transition: 'border-color 0.2s',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  inputDisabled: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    cursor: 'not-allowed',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px',
    backgroundColor: '#fff',
    color: '#111827',
    lineHeight: '1.5',
    transition: 'border-color 0.2s',
  },
  textareaError: {
    borderColor: '#ef4444',
  },
  textareaDisabled: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    cursor: 'not-allowed',
  },
  errorText: {
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: '#ef4444',
  },
  helpText: {
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  error: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    border: '1px solid #ef4444',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1.5rem',
    borderTop: '1px solid #e5e7eb',
    flexShrink: 0, // Footer doesn't shrink, stays visible
    backgroundColor: '#fff', // Ensure footer has background for sticky effect
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  submitButton: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '0.375rem',
    backgroundColor: '#10b981',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  submitButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    backgroundColor: '#9ca3af',
  },
};

